import { useState, useEffect, useRef } from 'react';
import { FileText, User, MapPin, Lock, Save, GraduationCap, Edit, X } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import { getAccountDetails, updateAccountDetails } from '../api/services/accountService';

// ── Deep diff utility: returns only changed keys ──
const getChangedFields = (original, current) => {
  const changes = {};
  for (const key in current) {
    if (typeof current[key] === 'object' && current[key] !== null && !Array.isArray(current[key])) {
      const nested = getChangedFields(original[key] || {}, current[key]);
      if (Object.keys(nested).length > 0) {
        changes[key] = nested;
      }
    } else if (current[key] !== original[key]) {
      changes[key] = current[key];
    }
  }
  return changes;
};
const inputCls = (disabled, hasError) =>
  `w-full px-4 py-3 bg-text-primary border rounded-lg text-text-main font-[inherit] !text-sm m-0 text-md outline-none cursor-not-allowed pointe${hasError ? 'border-red-500' : 'border-purple-glow-30'} ${disabled ? 'cursor-not-allowed pointer-events-none' : 'cursor-text'}`;

const SectionCard = ({ icon: Icon, iconBg, title, children }) => (
  <div className="p-3 mb-3">
    <div className="flex items-center gap-3 mb-3">
      <div className={`p-2 ${iconBg} rounded-lg`}><Icon size={20} /></div>
      <h2 className="text-lg font-semibold text-text-main">{title}</h2>
    </div>
    {children}
  </div>
);

function Account() {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [errors, setErrors] = useState({});

  // Store original API data to diff against
  const originalData = useRef(null);

  const [universityDetails, setUniversityDetails] = useState({
    universityName: '',
    shortName: '',
  });

  const [adminDetails, setAdminDetails] = useState({
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    mobileNumber: '',
    designation: '',
  });

  const [contactDetails, setContactDetails] = useState({
    officialEmail: '',
    phoneNumber: '',
    websiteUrl: '',
    country: '',
    stateProvince: '',
    city: '',
    zipPincode: '',
    address: '',
  });

  useEffect(() => {
    fetchAccount();
  }, []);

  // Build the full payload shape from current state (for diffing)
  const buildFullPayload = () => ({
    university_details: {
      name: universityDetails.universityName,
      code: universityDetails.shortName,
      contact_email: contactDetails.officialEmail,
      phone: contactDetails.phoneNumber,
      website: contactDetails.websiteUrl,
      location: {
        country: contactDetails.country,
        state: contactDetails.stateProvince,
        city: contactDetails.city,
        zip: contactDetails.zipPincode,
        address: contactDetails.address,
      },
    },
    admin_details: {
      first_name: adminDetails.adminFirstName,
      last_name: adminDetails.adminLastName,
      email: adminDetails.adminEmail,
      phone_number: adminDetails.mobileNumber,
      designation: adminDetails.designation,
    },
  });

  const fetchAccount = async () => {
    setIsLoading(true);
    setFetchError(null);
    try {
      const response = await getAccountDetails();
      if (!response.success) throw new Error('Failed to fetch account details');

      const uni = response.data.university_details || {};
      const admin = response.data.admin_details || {};
      const location = uni.location || {};

      setUniversityDetails({
        universityName: uni.name || '',
        shortName: uni.code || '',
      });

      setAdminDetails({
        adminFirstName: admin.first_name || '',
        adminLastName: admin.last_name || '',
        adminEmail: admin.email || '',
        mobileNumber: admin.phone_number || '',
        designation: admin.designation || '',
      });

      setContactDetails({
        officialEmail: uni.contact_email || '',
        phoneNumber: uni.phone || '',
        websiteUrl: uni.website || '',
        country: location.country || '',
        stateProvince: location.state || '',
        city: location.city || '',
        zipPincode: location.zip || '',
        address: location.address || '',
      });

      // Store original for diffing later
      originalData.current = {
        university_details: {
          name: uni.name || '',
          code: uni.code || '',
          contact_email: uni.contact_email || '',
          phone: uni.phone || '',
          website: uni.website || '',
          location: {
            country: location.country || '',
            state: location.state || '',
            city: location.city || '',
            zip: location.zip || '',
            address: location.address || '',
          },
        },
        admin_details: {
          first_name: admin.first_name || '',
          last_name: admin.last_name || '',
          email: admin.email || '',
          phone_number: admin.phone_number || '',
          designation: admin.designation || '',
        },
      };
    } catch (error) {
      console.error('Error fetching account:', error);
      setFetchError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const validatePhoneNumber = (phone) => {
    const cleaned = phone.replace(/[\s\-\(\)]/g, '');
    const regex = /^(\+?\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$|^\+\d{10,15}$/;
    return regex.test(cleaned) && cleaned.length >= 10 && cleaned.length <= 15;
  };

  const handleChange = (setter, prefix) => (e) => {
    const { name, value } = e.target;
    setter(prev => ({ ...prev, [name]: value }));
    if (name === 'mobileNumber' || name === 'phoneNumber') {
      if (!value.trim()) setErrors(prev => ({ ...prev, [`${prefix}_${name}`]: 'Required' }));
      else if (!validatePhoneNumber(value)) setErrors(prev => ({ ...prev, [`${prefix}_${name}`]: 'Invalid phone number' }));
      else setErrors(prev => ({ ...prev, [`${prefix}_${name}`]: '' }));
    } else if (errors[`${prefix}_${name}`]) {
      setErrors(prev => ({ ...prev, [`${prefix}_${name}`]: '' }));
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!adminDetails.mobileNumber.trim()) newErrors.admin_mobileNumber = 'Required';
    else if (!validatePhoneNumber(adminDetails.mobileNumber)) newErrors.admin_mobileNumber = 'Invalid';
    if (!contactDetails.phoneNumber.trim()) newErrors.contact_phoneNumber = 'Required';
    else if (!validatePhoneNumber(contactDetails.phoneNumber)) newErrors.contact_phoneNumber = 'Invalid';
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setIsSaving(true);
    try {
      const currentPayload = buildFullPayload();
      const changedFields = getChangedFields(originalData.current || {}, currentPayload);

      // Nothing changed
      if (Object.keys(changedFields).length === 0) {
        setIsEditing(false);
        setErrors({});
        return;
      }

      console.log('Sending only changed fields:', changedFields);

      const response = await updateAccountDetails(changedFields);
      if (!response.success) throw new Error('Failed to save profile');

      // Update original ref so next edit diffs from new state
      originalData.current = currentPayload;
      setIsEditing(false);
      setErrors({});
    } catch (error) {
      console.error('Error saving profile:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setErrors({});
    fetchAccount();
  };


  if (isLoading) {
    return (
      <div className="p-8 bg-screen-bg flex items-center justify-center h-full">
        <LoadingSpinner size="lg" text="Loading account details..." />
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="p-8 bg-screen-bg flex flex-col items-center justify-center h-full gap-4">
        <p className="text-red-500 text-lg">{fetchError}</p>
        <button onClick={fetchAccount} className="px-4 py-2 rounded-lg text-btn-text font-semibold bg-primary cursor-pointer">Retry</button>
      </div>
    );
  }

  return (
    <div className="p-8 bg-screen-bg">
      {/* Header */}
      <div className="mb-4 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-lg" style={{ background: 'linear-gradient(135deg, var(--buttons-background-color), #e879f9)' }}>
            <GraduationCap size={24} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-text-main">Account Management</h1>
            <p className="text-text-sub">Manage your university profile and account settings</p>
          </div>
        </div>
        {!isEditing &&(
          <button onClick={() => setIsEditing(true)} className="px-4 py-2 rounded-lg text-btn-text font-semibold bg-primary shadow-glow-btn transition-all flex items-center gap-2 hover:shadow-glow-md hover:scale-105 active:scale-100 cursor-pointer">
            <Edit size={18} /><span>Edit Profile</span>
          </button>
        )}
      </div>

      <form onSubmit={handleSaveProfile}>
      {isEditing && (
            <div className="flex justify-end gap-3 mt-2 mb-2 border-t border-purple-glow-15">
              <button type="button" onClick={handleCancelEdit} className="px-6 py-3 rounded-lg border border-purple-glow-20 text-btn-text bg-primary shadow-glow-btn hover:shadow-glow-md hover:scale-105 active:scale-100 cursor-pointer">Cancel</button>
              <button type="submit" disabled={isSaving} className="px-6 py-3 rounded-lg text-btn-text font-semibold bg-primary shadow-glow-btn transition-all flex items-center gap-2 hover:shadow-glow-md hover:scale-105 active:scale-100 cursor-pointer disabled:opacity-50">
                {isSaving ? <><LoadingSpinner size={16} /><span>Saving...</span></> : <><Save size={18} /><span>Save Profile</span></>}
              </button>
            </div>  
              )}  
        {/* University Details */}
        <SectionCard icon={FileText} iconBg="bg-blue-100 text-blue-600" title="University Details">
        <div className="bg-card-gradient rounded-lg shadow-glow-sm border border-purple-glow-20 p-6 mb-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-text-main mb-2">University Name <span className="text-red-500">*</span></label>
              <input type="text" name="universityName" value={universityDetails.universityName} onChange={handleChange(setUniversityDetails, 'university')} required  className={inputCls(!isEditing)} />
            </div>
            <div>
              <label className="block text-sm font-semibold text-text-main mb-2">Short Name / Code <span className="text-red-500">*</span></label>
              <input type="text" name="shortName" value={universityDetails.shortName} onChange={handleChange(setUniversityDetails, 'university')} required  className={inputCls(!isEditing)} />
            </div>
          </div>
          </div>
        </SectionCard>

        {/* Admin Details */}
        <SectionCard icon={User} iconBg="bg-green-100 text-green-600" title="Admin Details">
        <div className="bg-card-gradient rounded-lg shadow-glow-sm border border-purple-glow-20 p-6 mb-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-text-main mb-2">First Name <span className="text-red-500">*</span></label>
              <input type="text" name="adminFirstName" value={adminDetails.adminFirstName} onChange={handleChange(setAdminDetails, 'admin')} className={inputCls(!isEditing)} />
            </div>
            <div>
              <label className="block text-sm font-semibold text-text-main mb-2">Last Name <span className="text-red-500">*</span></label>
              <input type="text" name="adminLastName" value={adminDetails.adminLastName} onChange={handleChange(setAdminDetails, 'admin')}  className={inputCls(!isEditing)} />
            </div>
            <div>
              <label className="block text-sm font-semibold text-text-main mb-2">Email</label>
              <input type="email" name="adminEmail" value={adminDetails.adminEmail} onChange={handleChange(setAdminDetails, 'admin')} className={inputCls(!isEditing)} />
            </div>
            <div>
              <label className="block text-sm font-semibold text-text-main mb-2">Mobile Number <span className="text-red-500">*</span></label>
              <input type="tel" name="mobileNumber" value={adminDetails.mobileNumber} onChange={handleChange(setAdminDetails, 'admin')} className={inputCls(!isEditing, !!errors.admin_mobileNumber)} />
              {errors.admin_mobileNumber && <p className="text-red-500 text-xs mt-1">⚠ {errors.admin_mobileNumber}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-text-main mb-2">Designation <span className="text-red-500">*</span></label>
              <input type="text" name="designation" value={adminDetails.designation} onChange={handleChange(setAdminDetails, 'admin')}  className={inputCls(!isEditing)} />
            </div>
          </div>
          </div>
        </SectionCard>

        {/* University Contact Details */}
        <SectionCard icon={MapPin} iconBg="bg-purple-glow-15 text-primary" title="University Contact Details">
        <div className="bg-card-gradient rounded-lg shadow-glow-sm border border-purple-glow-20 p-6 mb-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-text-main mb-2">Official Email <span className="text-red-500">*</span></label>
              <input type="email" name="officialEmail" value={contactDetails.officialEmail} onChange={handleChange(setContactDetails, 'contact')} className={inputCls(!isEditing)} />
            </div>
            <div>
              <label className="block text-sm font-semibold text-text-main mb-2">Phone Number <span className="text-red-500">*</span></label>
              <input type="tel" name="phoneNumber" value={contactDetails.phoneNumber} onChange={handleChange(setContactDetails, 'contact')} className={inputCls(!isEditing, !!errors.contact_phoneNumber)} />
              {errors.contact_phoneNumber && <p className="text-red-500 text-xs mt-1">⚠ {errors.contact_phoneNumber}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-text-main mb-2">Website URL</label>
              <input type="url" name="websiteUrl" value={contactDetails.websiteUrl} onChange={handleChange(setContactDetails, 'contact')} className={inputCls(!isEditing)} />
            </div>
            <div>
              <label className="block text-sm font-semibold text-text-main mb-2">Country <span className="text-red-500">*</span></label>
              <input type="text" name="country" value={contactDetails.country} onChange={handleChange(setContactDetails, 'contact')} className={inputCls(!isEditing)} />
            </div>
            <div>
              <label className="block text-sm font-semibold text-text-main mb-2">State/Province <span className="text-red-500">*</span></label>
              <input type="text" name="stateProvince" value={contactDetails.stateProvince} onChange={handleChange(setContactDetails, 'contact')} className={inputCls(!isEditing)} />
            </div>
            <div>
              <label className="block text-sm font-semibold text-text-main mb-2">City <span className="text-red-500">*</span></label>
              <input type="text" name="city" value={contactDetails.city} onChange={handleChange(setContactDetails, 'contact')} className={inputCls(!isEditing)} />
            </div>
            <div>
              <label className="block text-sm font-semibold text-text-main mb-2">ZIP/Pincode <span className="text-red-500">*</span></label>
              <input type="text" name="zipPincode" value={contactDetails.zipPincode} onChange={handleChange(setContactDetails, 'contact')} className={inputCls(!isEditing)} />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-text-main mb-2">Address <span className="text-red-500">*</span></label>
              <textarea name="address" value={contactDetails.address} onChange={handleChange(setContactDetails, 'contact')} rows={3} className={`${inputCls(!isEditing)} resize-none`} />
            </div>
          </div>
          </div>
        </SectionCard>
      </form>
    </div>
  );
}

export default Account;